/**
 * Public survey runner — CancerX baseline and any survey that follows.
 *
 * Unauthenticated by design: respondents are client staff with no app
 * account. The gate is a work-email domain allowlist enforced server-side;
 * this page only reflects the server's verdict.
 *
 * The questions are not hard-coded here. The server returns the survey
 * definition and this renders whatever it gets, so wording changes ship
 * without a frontend build.
 *
 * One question per screen, as specified. Answers save to the server as the
 * respondent advances, so a closed tab resumes rather than starting over.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import api from '../services/api';

type QType = 'single' | 'multi' | 'scale' | 'short_text' | 'long_text';

interface Question {
  id: string;
  type: QType;
  text: string;
  section?: string;
  help?: string;
  optional?: boolean;
  options?: (string | { value: string; label: string })[];
  max_select?: number;
  scale_low?: string;
  scale_high?: string;
  show_if?: { question: string; includes: string };
}

interface Branch { label: string; questions: Question[] }

interface Definition {
  key: string;
  welcome: { title: string; body: string };
  closing: { title: string; body: string };
  routing_question: Question;
  branches: Record<string, Branch>;
  closing_questions: Question[];
  allowed_domains: string[];
}

type Answers = Record<string, string | string[]>;

const optValue = (o: string | { value: string; label: string }) =>
  typeof o === 'string' ? o : o.value;
const optLabel = (o: string | { value: string; label: string }) =>
  typeof o === 'string' ? o : o.label;

const SurveyPage: React.FC = () => {
  const { surveyKey = 'cancerx_baseline' } = useParams<{ surveyKey: string }>();

  const [def, setDef] = useState<Definition | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [gateError, setGateError] = useState<string | null>(null);
  const [gateBusy, setGateBusy] = useState(false);
  const [started, setStarted] = useState(false);

  const [answers, setAnswers] = useState<Answers>({});
  const [step, setStep] = useState(-1); // -1 = welcome
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/surveys/${surveyKey}`);
        setDef(res.data);
      } catch {
        setLoadError('This survey could not be loaded.');
      }
    })();
  }, [surveyKey]);

  const branchKey = String(answers[def?.routing_question.id || 'role'] || '');

  // The question list is derived, not stored: picking a different role
  // re-derives the whole path rather than leaving orphaned answers behind.
  const questions: Question[] = useMemo(() => {
    if (!def) return [];
    const list: Question[] = [def.routing_question];
    const branch = def.branches[branchKey];
    if (branch) list.push(...branch.questions, ...def.closing_questions);
    return list.filter((q) => {
      if (!q.show_if) return true;
      const dep = answers[q.show_if.question];
      return Array.isArray(dep) && dep.includes(q.show_if.includes);
    });
  }, [def, branchKey, answers]);

  const current = step >= 0 ? questions[step] : null;
  const isLast = step === questions.length - 1;
  const progress = questions.length > 1
    ? Math.round((Math.max(step, 0) / (questions.length - 1)) * 100)
    : 0;

  const persist = useCallback(
    async (next: Answers, complete = false) => {
      try {
        await api.post(`/public/surveys/${surveyKey}/save`, {
          email,
          answers: next,
          branch: next[def?.routing_question.id || 'role'] || undefined,
          complete,
        });
      } catch {
        // Saving is best-effort mid-survey — losing one autosave shouldn't
        // interrupt someone mid-thought. Submit surfaces its own errors.
      }
    },
    [surveyKey, email, def],
  );

  const beginSurvey = async () => {
    setGateBusy(true);
    setGateError(null);
    try {
      const res = await api.post(`/public/surveys/${surveyKey}/start`, { email });
      setAnswers(res.data.answers || {});
      setStarted(true);
      if (res.data.is_complete) setDone(true);
    } catch (e: any) {
      setGateError(e?.response?.data?.detail || 'Could not start the survey.');
    } finally {
      setGateBusy(false);
    }
  };

  const setAnswer = (id: string, value: string | string[]) =>
    setAnswers((prev) => ({ ...prev, [id]: value }));

  const toggleMulti = (q: Question, value: string) => {
    const cur = Array.isArray(answers[q.id]) ? (answers[q.id] as string[]) : [];
    let next: string[];
    if (cur.includes(value)) {
      next = cur.filter((v) => v !== value);
    } else if (q.max_select && cur.length >= q.max_select) {
      // At the cap, replace the oldest rather than silently ignoring the
      // click — an unresponsive checkbox reads as broken.
      next = [...cur.slice(1), value];
    } else {
      next = [...cur, value];
    }
    setAnswer(q.id, next);
  };

  const goNext = async () => {
    const next = answers;
    if (isLast) {
      setSaving(true);
      await persist(next, true);
      setSaving(false);
      setDone(true);
      return;
    }
    void persist(next);
    setStep((s) => s + 1);
  };

  if (loadError) {
    return <Shell><p className="text-gray-600">{loadError}</p></Shell>;
  }
  if (!def) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full bg-green-50 text-green-600 flex items-center justify-center mx-auto mb-4">
            <Check className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">{def.closing.title}</h1>
          <p className="text-gray-600 leading-relaxed whitespace-pre-line max-w-xl mx-auto">
            {def.closing.body}
          </p>
        </div>
      </Shell>
    );
  }

  // --- Email gate -------------------------------------------------------
  if (!started) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">{def.welcome.title}</h1>
        <p className="text-gray-600 leading-relaxed whitespace-pre-line mb-8">
          {def.welcome.body}
        </p>
        <label htmlFor="survey-email" className="block text-sm font-medium text-gray-900 mb-1.5">
          Enter your work email to begin
        </label>
        <input
          id="survey-email"
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setGateError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && email) void beginSurvey(); }}
          placeholder="you@yourorganization.org"
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-xs text-gray-500 mt-2">
          Accepted domains: {def.allowed_domains.join(', ')}
        </p>
        {gateError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mt-3">
            {gateError}
          </p>
        )}
        <button
          type="button"
          onClick={beginSurvey}
          disabled={!email || gateBusy}
          className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {gateBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Start survey
        </button>
      </Shell>
    );
  }

  // --- Welcome (after gate) --------------------------------------------
  if (step < 0) {
    return (
      <Shell>
        <p className="text-sm text-gray-500 mb-2">Signed in as {email}</p>
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">{def.welcome.title}</h1>
        <p className="text-gray-600 leading-relaxed whitespace-pre-line mb-8">
          {def.welcome.body}
        </p>
        <button
          type="button"
          onClick={() => setStep(0)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          Begin <ArrowRight className="w-4 h-4" />
        </button>
      </Shell>
    );
  }

  if (!current) return <Shell><p className="text-gray-600">Nothing to show.</p></Shell>;

  const answered =
    current.type === 'multi'
      ? Array.isArray(answers[current.id]) && (answers[current.id] as string[]).length > 0
      : Boolean(answers[current.id]);

  return (
    <Shell>
      <div className="mb-6">
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>{current.section || ''}</span>
          <span>{step + 1} of {questions.length}</span>
        </div>
      </div>

      <h2 className="text-xl font-medium text-gray-900 leading-snug mb-2">{current.text}</h2>
      {current.help && <p className="text-sm text-gray-500 mb-5">{current.help}</p>}
      {current.max_select && (
        <p className="text-sm text-gray-500 mb-5">Pick up to {current.max_select}.</p>
      )}

      <div className="space-y-2 mb-8">
        {current.type === 'single' && (current.options || []).map((o) => {
          const v = optValue(o);
          const sel = answers[current.id] === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => setAnswer(current.id, v)}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                sel ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              {optLabel(o)}
            </button>
          );
        })}

        {current.type === 'multi' && (current.options || []).map((o) => {
          const v = optValue(o);
          const cur = Array.isArray(answers[current.id]) ? (answers[current.id] as string[]) : [];
          const sel = cur.includes(v);
          return (
            <button
              key={v}
              type="button"
              onClick={() => toggleMulti(current, v)}
              className={`w-full text-left px-4 py-3 rounded-lg border flex items-center gap-3 transition-colors ${
                sel ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                sel ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
              }`}>
                {sel && <Check className="w-3 h-3 text-white" />}
              </span>
              {optLabel(o)}
            </button>
          );
        })}

        {current.type === 'scale' && (
          <div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setAnswer(current.id, String(n))}
                  className={`flex-1 py-3 rounded-lg border font-medium transition-colors ${
                    answers[current.id] === String(n)
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{current.scale_low}</span>
              <span>{current.scale_high}</span>
            </div>
          </div>
        )}

        {current.type === 'short_text' && (
          <input
            type="text"
            value={(answers[current.id] as string) || ''}
            onChange={(e) => setAnswer(current.id, e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        )}

        {current.type === 'long_text' && (
          <textarea
            rows={5}
            value={(answers[current.id] as string) || ''}
            onChange={(e) => setAnswer(current.id, e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(s - 1, 0))}
          disabled={step === 0}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-3">
          {/* Nothing is required — an unanswered question is still data,
              and this is a baseline exercise, not an audit. */}
          {!answered && !current.optional && (
            <span className="text-xs text-gray-400">Skip if it doesn't apply</span>
          )}
          <button
            type="button"
            onClick={goNext}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isLast ? 'Submit' : 'Next'}
            {!isLast && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </Shell>
  );
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gray-50 py-10 px-4">
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
          V
        </div>
        <span className="font-semibold text-gray-900">VentureOS</span>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">{children}</div>
      <p className="text-center text-xs text-gray-400 mt-6">
        Your answers are stored securely and used to configure the platform for your team.
      </p>
    </div>
  </div>
);

export default SurveyPage;
